import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, MonitorSmartphone, WifiOff, CloudUpload, CheckCircle2, AlertTriangle, HelpCircle, ChevronDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { api } from "../lib/api";

/**
 * Sync Health board (Phase 52) — shows which collectors have unsynced offline
 * field work on their devices, based on device heartbeats.
 * Phase 128: `collapsible` renders the header as a toggle (collapsed by default unless `defaultOpen`);
 * data still loads in the background so the "needs attention" count is visible while collapsed.
 */

const STATUS_META = {
  stuck: { label: "Stuck", cls: "bg-red-100 text-red-800 border-red-200", icon: AlertTriangle,
    hint: "Has unsynced work and the device hasn't been online recently — data may be stuck" },
  failed: { label: "Sync failed", cls: "bg-red-50 text-red-700 border-red-200", icon: AlertTriangle,
    hint: "Some items failed to sync and need attention" },
  pending: { label: "Pending", cls: "bg-amber-50 text-amber-800 border-amber-200", icon: CloudUpload,
    hint: "Items are waiting on the device and should sync shortly" },
  offline: { label: "Not seen recently", cls: "bg-slate-100 text-slate-700 border-slate-200", icon: WifiOff,
    hint: "Device hasn't reported recently — it may be offline holding unreported work" },
  never_reported: { label: "No data", cls: "bg-muted text-muted-foreground border-transparent", icon: HelpCircle,
    hint: "This collector's app has not reported sync status yet" },
  ok: { label: "All synced", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2,
    hint: "Queue is empty and the device was seen recently" },
};

const KIND_LABELS = { survey: "surveys", reassessment: "reassessments", visit: "visits", mark_visited: "visit marks", mobile_verify: "mobile verifications" };

const relTime = (iso) => {
  if (!iso) return "never";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const kindSummary = (kinds) => Object.entries(kinds || {})
  .filter(([, n]) => n > 0)
  .map(([k, n]) => `${n} ${KIND_LABELS[k] || k}`)
  .join(", ");

export const SyncHealthCard = ({ code, testIdPrefix = "sync-health", collapsible = false, defaultOpen = true }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(collapsible ? defaultOpen : true);

  const load = useCallback(() => {
    setBusy(true);
    api.get(`/t/${code}/field/sync-health`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setBusy(false));
  }, [code]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};
  const items = data?.items || [];
  const attention = (summary.stuck || 0) + (summary.failed || 0) + (summary.pending || 0);

  const attentionBadge = attention > 0 && (
    <span className="text-xs font-semibold bg-red-100 text-red-700 rounded-full px-2 py-0.5"
      data-testid={`${testIdPrefix}-attention-count`}>
      {attention} need{attention === 1 ? "s" : ""} attention
    </span>
  );

  const description = (
    <p className="text-xs text-muted-foreground">
      Offline field work saved on collector devices — pending, failed, or unreported sync. Unsynced items across team:{" "}
      <span className="font-semibold" data-testid={`${testIdPrefix}-unsynced-total`}>{summary.unsynced_items || 0}</span>
    </p>
  );

  const refreshBtn = (
    <Button variant="outline" size="sm" className="h-8" onClick={(e) => { e.stopPropagation(); load(); }} disabled={busy}
      data-testid={`${testIdPrefix}-refresh-button`}>
      <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
    </Button>
  );

  const body = !data ? (
    <p className="text-sm text-muted-foreground py-2" data-testid={`${testIdPrefix}-loading`}>Loading device sync health…</p>
  ) : (
    <>
      {items.length === 0 && (
        <p className="text-sm text-muted-foreground py-2" data-testid={`${testIdPrefix}-empty`}>
          No collectors in your team yet.
        </p>
      )}
      <TooltipProvider delayDuration={200}>
        {items.map((it) => {
          const meta = STATUS_META[it.status] || STATUS_META.never_reported;
          const Icon = meta.icon;
          const backlog = (it.pending_count || 0) + (it.failed_count || 0);
          return (
            <div key={it.collector_id}
              className="flex items-center justify-between gap-3 border rounded-lg px-3 py-2"
              data-testid={`${testIdPrefix}-row-${it.collector_id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">
                  {it.name}{it.is_sub_user ? <span className="text-[10px] text-muted-foreground ml-1">(sub-user)</span> : null}
                </p>
                <p className="text-[11px] text-muted-foreground truncate">
                  Last seen {relTime(it.reported_at)}
                  {backlog > 0 && it.oldest_pending_at ? ` • oldest item ${relTime(it.oldest_pending_at)}` : ""}
                  {backlog > 0 && kindSummary(it.kinds) ? ` • ${kindSummary(it.kinds)}` : ""}
                </p>
                {it.labels?.length > 0 && backlog > 0 && (
                  <p className="text-[11px] text-muted-foreground truncate" title={it.labels.join(" | ")}>
                    {it.labels.join(" | ")}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {backlog > 0 && (
                  <span className="text-xs font-semibold text-amber-800" data-testid={`${testIdPrefix}-backlog-${it.collector_id}`}>
                    {backlog} item{backlog === 1 ? "" : "s"}
                  </span>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className={`inline-flex items-center gap-1 text-xs font-medium border rounded-full px-2 py-1 ${meta.cls}`}
                      data-testid={`${testIdPrefix}-status-${it.collector_id}`}>
                      <Icon className="h-3 w-3" /> {meta.label}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="max-w-56 text-xs">{meta.hint}</TooltipContent>
                </Tooltip>
              </div>
            </div>
          );
        })}
      </TooltipProvider>
    </>
  );

  if (!collapsible) {
    return (
      <Card className="shadow-sm" data-testid={`${testIdPrefix}-card`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading flex items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <MonitorSmartphone className="h-4 w-4" /> Device sync health
              {attentionBadge}
            </span>
            {refreshBtn}
          </CardTitle>
          {description}
        </CardHeader>
        <CardContent className="space-y-1.5">{body}</CardContent>
      </Card>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="shadow-sm" data-testid={`${testIdPrefix}-card`} data-state={open ? "open" : "closed"}>
        <CardHeader className="p-0">
          <div className="flex items-center gap-2 pr-3">
            <CollapsibleTrigger asChild>
              <button type="button"
                className="flex-1 flex items-center justify-between gap-3 text-left px-4 py-3 rounded-t-lg hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={open ? "Collapse device sync health" : "Expand device sync health"}
                data-testid={`${testIdPrefix}-toggle`}>
                <span className="min-w-0">
                  <CardTitle className="text-base font-heading flex items-center gap-2">
                    <MonitorSmartphone className="h-4 w-4" /> Device sync health
                    {attentionBadge}
                    {data && attention === 0 && (
                      <span className="text-xs font-medium text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5"
                        data-testid={`${testIdPrefix}-all-clear`}>All synced</span>
                    )}
                  </CardTitle>
                  {open ? description : (
                    <p className="text-xs text-muted-foreground mt-0.5" data-testid={`${testIdPrefix}-collapsed-hint`}>
                      {data ? `${items.length} collector${items.length === 1 ? "" : "s"} • ${summary.unsynced_items || 0} unsynced item${(summary.unsynced_items || 0) === 1 ? "" : "s"} • click to expand`
                        : "Loading device sync health…"}
                    </p>
                  )}
                </span>
                <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
              </button>
            </CollapsibleTrigger>
            {open && refreshBtn}
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="space-y-1.5 pt-1" data-testid={`${testIdPrefix}-content`}>{body}</CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};
